import { Middleware } from '@reduxjs/toolkit';
import { t } from 'i18next';

import {
  confirmPaymentFailure,
  fetchCertainPaymentMethodFailure,
  fetchCertainPaymentMethodSuccess,
  fetchPaymentCurrentStateSuccess,
  fetchPaymentInformationSuccess,
  processPaymentFailure,
  refetchCertainPaymentMethodFailure,
  refetchCertainPaymentMethodSuccess,
  registerPaymentFailure,
  registerPaymentSuccess,
  setActiveScreen,
  setPaymentResult,
} from '@cashier/cashier-data-access';
import {
  DynamicScreens,
  FinalStates,
  PaymentActionError,
  PaymentTypes,
  RedirectContainers,
  ResultMessageTypes,
} from '@cashier/cashier-model';
import { PaymentStateChecker } from '@cashier/cashier-shared';

export const paymentResultMiddleware: Middleware =
  (store) => (next) => (action) => {
    const result = next(action);

    const {
      cashierConfiguration: { paymentType },
      paymentMethod: {
        paymentMethod: { final_screen_description, redirectContainer },
      },
      paymentProcessing: {
        result: { resultAction: processingResultAction },
      },
    } = store.getState();

    const translatedPaymentTypeMap: { [key: string]: string } = {
      [PaymentTypes.Deposit]: t('Deposit'),
      [PaymentTypes.Withdrawal]: t('Withdrawal'),
    };

    if (
      [
        processPaymentFailure.toString(),
        confirmPaymentFailure.toString(),
      ].includes(action.type)
    ) {
      const { context }: PaymentActionError = action.payload;

      if (!context || context.ui.display === 'screen') {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.ERROR,
            message: {
              content: action.payload?.message,
              type: ResultMessageTypes.HTML,
            },
          }),
        );
      }
    }

    if (
      [
        fetchPaymentCurrentStateSuccess.toString(),
        fetchPaymentInformationSuccess.toString(),
      ].includes(action.type)
    ) {
      const { state } = action.payload;

      if (PaymentStateChecker.isSuccess(paymentType, state)) {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.SUCCESS,
            message: {
              content: t('successfulTransactionComplex.message', {
                transactionType: translatedPaymentTypeMap[paymentType],
              }),
              type: ResultMessageTypes.STRING,
            },
          }),
        );
      }

      if (PaymentStateChecker.isFailed(paymentType, state)) {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.ERROR,
            message: {
              content: t('failedTransaction.message'),
              type: ResultMessageTypes.STRING,
            },
          }),
        );
      }

      if (
        PaymentStateChecker.isPending(paymentType, state) &&
        !processingResultAction
      ) {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.PENDING,
            message: {
              content: final_screen_description,
              type: ResultMessageTypes.HTML,
            },
          }),
        );
      }
    }

    if (
      action.type === registerPaymentSuccess.toString() &&
      redirectContainer === RedirectContainers.NEW_TAB
    ) {
      store.dispatch(
        setPaymentResult({
          finalState: FinalStates.SUCCESS,
          message: {
            content: t('successfulRegistrationChannel.message'),
            type: ResultMessageTypes.STRING,
          },
        }),
      );
    }

    if (action.type === registerPaymentFailure.toString()) {
      const { context, message }: PaymentActionError = action.payload;

      if (!context || context.ui.display === 'screen') {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.ERROR,
            message: {
              content: message || t('failureRegistrationChannel.message'),
              type: ResultMessageTypes.STRING,
            },
          }),
        );
      }
    }

    if (
      [
        fetchCertainPaymentMethodFailure.toString(),
        refetchCertainPaymentMethodFailure.toString(),
      ].includes(action.type)
    ) {
      store.dispatch(
        setPaymentResult({
          finalState: FinalStates.PAYMENT_METHOD_ERROR,
          message: {
            content: action.payload,
            type: ResultMessageTypes.STRING,
          },
        }),
      );
    }

    if (
      [
        fetchCertainPaymentMethodSuccess.toString(),
        refetchCertainPaymentMethodSuccess.toString(),
      ].includes(action.type)
    ) {
      const {
        paymentMethod: {
          paymentMethod: { restriction },
        },
      } = store.getState();

      if (restriction) {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.RESTRICTED,
            message: {
              content: restriction.message,
              type: ResultMessageTypes.STRING,
            },
          }),
        );
        store.dispatch(setActiveScreen(DynamicScreens.FinalScreen));
      }
    }

    return result;
  };
